"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { listExercises, getExercise, type ExerciseListItem, type ExerciseDetail } from "@/lib/api/api-client";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { ModalTemplate } from "../../templates/modal-template";

/**
 * Volet occupant l'écran quand la liste et la fiche ne tiennent pas côte à
 * côte (< 768 px). Ce n'est pas une seconde sélection : `selectedId` reste la
 * seule source de vérité sur l'exercice courant. Aucun effet visible au-delà
 * de 768 px, où les deux volets restent affichés en permanence.
 */
type CompactPane = "list" | "detail";

// ─── Constants ───

const CATEGORY_META: Record<string, { label: string; color: string; hue: number }> = {
  gamme:         { label: "Gammes",                  color: "#4ade80", hue: 140 },
  arpège:        { label: "Arpèges",                 color: "#60a5fa", hue: 215 },
  accord:        { label: "Accords",                  color: "#a78bfa", hue: 260 },
  rythme:        { label: "Rythme",                   color: "#fbbf24", hue: 45  },
  déchiffrage:   { label: "Déchiffrage",              color: "#f472b6", hue: 330 },
  indépendance:  { label: "Indépendance des mains",   color: "#2dd4bf", hue: 170 },
};

const DEFAULT_CATEGORY = { label: "Autre", color: "#94a3b8", hue: 210 };

const DIFFICULTY_LABELS: Record<number, string> = {
  1: "Débutant", 2: "Facile", 3: "Intermédiaire", 4: "Avancé", 5: "Expert",
};

const DIFFICULTY_COLORS: Record<number, string> = {
  1: "#4ade80", 2: "#86efac", 3: "#a78bfa", 4: "#fbbf24", 5: "#f87171",
};

function getCategoryMeta(category: string | null) {
  if (!category) return DEFAULT_CATEGORY;
  return CATEGORY_META[category] ?? DEFAULT_CATEGORY;
}

// ─── Small components ───

function DifficultyDots({ level }: { level: number }) {
  return (
    <div className="flex gap-[3px] items-center" aria-label={DIFFICULTY_LABELS[level]}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="w-[5px] h-[5px] rounded-full"
          style={{ backgroundColor: i <= level ? (DIFFICULTY_COLORS[level] ?? "#94a3b8") : "rgba(255,255,255,0.08)" }} />
      ))}
    </div>
  );
}

function CategoryIcon({ category, size = 20 }: { category: string | null; size?: number }) {
  const s = size;
  switch (category) {
    case "gamme":
      return (<svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="M3 14l3-3 3 3 3-6 3 3 2-4" /></svg>);
    case "arpège":
      return (<svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="M3 16l4-5 3 3 4-7 3 4" /><circle cx="3" cy="16" r="1" fill="currentColor" /><circle cx="7" cy="11" r="1" fill="currentColor" /><circle cx="10" cy="14" r="1" fill="currentColor" /><circle cx="14" cy="7" r="1" fill="currentColor" /><circle cx="17" cy="11" r="1" fill="currentColor" /></svg>);
    case "accord":
      return (<svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><rect x="4" y="6" width="12" height="9" rx="1.5" /><path d="M7 10v2M10 8v4M13 9v3" /></svg>);
    case "rythme":
      return (<svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><circle cx="6" cy="13" r="2.5" /><path d="M8.5 13V4" /><circle cx="14" cy="12" r="2.5" /><path d="M16.5 12V5" /><path d="M8.5 4h8l-.5 2" /></svg>);
    case "déchiffrage":
      return (<svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="M4 5h12M4 8h10M4 11h8M4 14h11" /><circle cx="16" cy="11" r="1.5" fill="currentColor" opacity="0.5" /></svg>);
    case "indépendance":
      return (<svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="M4 7c2-3 4-3 6 0" /><path d="M10 7c2-3 4-3 6 0" /><path d="M4 13c2 3 4 3 6 0" /><path d="M10 13c2 3 4 3 6 0" /></svg>);
    default:
      return (<svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><circle cx="10" cy="10" r="6" /><path d="M10 7v3l2 2" /></svg>);
  }
}

function CategoryTab({ active, onClick, color, children }: {
  active: boolean; onClick: () => void; color?: string; children: React.ReactNode;
}) {
  return (
    <button onClick={onClick}
      className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 cursor-pointer
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
        ${active ? "shadow-sm" : "text-text-muted hover:text-text-secondary hover:bg-white/[0.04]"}`}
      style={active ? {
        background: `${color || "var(--color-accent)"}15`,
        color: color || "var(--color-accent)",
        border: `1px solid ${color || "var(--color-accent)"}20`,
      } : { border: "1px solid transparent" }}>
      {children}
    </button>
  );
}

function ExerciseListItem({ exercise, selected, onClick }: {
  exercise: ExerciseListItem; selected: boolean; onClick: () => void;
}) {
  const cat = getCategoryMeta(exercise.category);
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-all duration-100 cursor-pointer
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent
        ${selected ? "bg-accent/[0.12]" : "hover:bg-white/[0.04]"}`}
      style={{ borderLeft: selected ? "3px solid var(--color-accent)" : "3px solid transparent" }}>
      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cat.color }} />
      <div className="flex-1 min-w-0">
        <span className={`text-[14px] font-medium truncate block ${selected ? "text-text-primary" : "text-text-secondary"}`}>
          {exercise.title}
        </span>
        <div className="flex items-center gap-2 mt-0.5">
          <DifficultyDots level={exercise.difficulty} />
        </div>
      </div>
    </button>
  );
}

function ExerciseDetailPanel({ exercise, onStart, loading }: {
  exercise: ExerciseListItem; onStart: () => void; loading: boolean;
}) {
  const cat = getCategoryMeta(exercise.category);
  const tags: string[] = [];

  return (
    <div className="flex flex-1 flex-col min-h-0" key={exercise.id}>
      <div className="flex-1 overflow-y-auto px-5 md:px-8 pt-8 pb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, hsla(${cat.hue}, 40%, 18%, 0.8), hsla(${cat.hue + 30}, 35%, 12%, 0.6))`,
              border: `1px solid hsla(${cat.hue}, 50%, 40%, 0.2)`,
              color: cat.color,
            }}>
            <CategoryIcon category={exercise.category} size={22} />
          </div>
          <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: cat.color }}>
            {cat.label}
          </span>
        </div>

        <h3 className="text-[22px] font-bold tracking-[-0.015em] mb-1"
          style={{ color: "rgba(255, 255, 255, 0.97)", textShadow: "0 0 40px rgba(139, 92, 246, 0.15)" }}>
          {exercise.title}
        </h3>
        {exercise.description && (
          <p className="text-[14px] text-text-secondary mb-6">{exercise.description}</p>
        )}

        <div className="h-px mb-6" style={{ background: "var(--color-glass-separator)" }} />

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {tags.map((tag) => (
              <span key={tag} className="text-[12px] px-2.5 py-1 rounded-lg"
                style={{
                  background: `hsla(${cat.hue}, 30%, 15%, 0.3)`,
                  border: `1px solid hsla(${cat.hue}, 40%, 30%, 0.15)`,
                  color: `hsla(${cat.hue}, 50%, 75%, 0.8)`,
                }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <DifficultyDots level={exercise.difficulty} />
            <span className="text-[12px] font-medium" style={{ color: DIFFICULTY_COLORS[exercise.difficulty] ?? "#94a3b8" }}>
              {DIFFICULTY_LABELS[exercise.difficulty] ?? `Niveau ${exercise.difficulty}`}
            </span>
          </div>
        </div>
      </div>

      <div className="shrink-0 px-5 md:px-8 pb-6 pt-2">
        <button onClick={onStart} disabled={loading}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-xl text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-wait
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-deep"
          style={{
            background: `linear-gradient(135deg, ${cat.color}, ${cat.color}cc)`,
            boxShadow: `0 4px 20px ${cat.color}25`,
          }}>
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Chargement...</>
          ) : (
            <><svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M4.5 2.5v11l9-5.5z" /></svg> Pratiquer</>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Modal content ───

function PracticeContent({ onStartExercise, compactPane, onCompactPaneChange }: {
  onStartExercise: (exercise: ExerciseDetail) => void;
  compactPane: CompactPane;
  /** Piloté par le parent, qui en a besoin pour rebrancher la touche Échap. */
  onCompactPaneChange: (pane: CompactPane) => void;
}) {
  const [exercises, setExercises] = useState<ExerciseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listExercises(1, 100);
      setExercises(res.items);
      if (res.items.length > 0) {
        setSelectedId(res.items[0].id);
      }
    } catch (err) {
      console.error("Failed to load exercises:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  // Unique categories from data
  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const ex of exercises) {
      if (ex.category) cats.add(ex.category);
    }
    return Array.from(cats).sort();
  }, [exercises]);

  const filtered = useMemo(() => {
    return exercises.filter((ex) => {
      if (categoryFilter !== "all" && ex.category !== categoryFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!ex.title.toLowerCase().includes(q) && !(ex.description ?? "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [exercises, categoryFilter, search]);

  useEffect(() => {
    if (filtered.length > 0 && !filtered.find((e) => e.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const selected = exercises.find((e) => e.id === selectedId);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-[13px]">Chargement des exercices...</p>
        </div>
      </div>
    );
  }

  if (exercises.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted">
        <p className="text-[14px]">Aucun exercice disponible</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Category tabs — filtres de la liste, donc ils s'effacent avec elle */}
      <div className={`${compactPane === "detail" ? "hidden md:flex" : "flex"} items-center gap-1 px-6 py-2.5 shrink-0 overflow-x-auto`}
        style={{ borderBottom: "1px solid var(--color-glass-separator)" }}>
        <CategoryTab active={categoryFilter === "all"} onClick={() => setCategoryFilter("all")}>
          Toutes <span className="text-text-muted/70 ml-0.5">{exercises.length}</span>
        </CategoryTab>
        {categories.map((cat) => {
          const meta = getCategoryMeta(cat);
          const count = exercises.filter((e) => e.category === cat).length;
          return (
            <CategoryTab key={cat} active={categoryFilter === cat} onClick={() => setCategoryFilter(cat)} color={meta.color}>
              <CategoryIcon category={cat} size={13} />
              {meta.label}
              <span style={{ opacity: 0.5 }}>{count}</span>
            </CategoryTab>
          );
        })}
      </div>

      {/* Master-detail — côte à côte dès 768 px, un volet à la fois en deçà */}
      <div className="flex-1 flex min-h-0">
        {/* Left panel, list */}
        <div className={`${compactPane === "detail" ? "hidden md:flex" : "flex"} flex-col w-full md:w-[280px] md:shrink-0 md:border-r md:border-glass-separator`}>
          <div className="px-3 py-2.5 shrink-0">
            <div className="relative">
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                  <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5L14 14" />
                </svg>
              </div>
              <input type="search" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full h-8 bg-white/[0.04] text-[13px] text-text-primary placeholder:text-text-muted rounded-md pl-8 pr-3
                  border border-border-subtle focus:border-accent/40 focus:outline-none focus:ring-1 focus:ring-accent/15 transition-all" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-text-muted">
                <p className="text-[13px]">Aucun exercice</p>
              </div>
            ) : filtered.map((ex) => (
              <ExerciseListItem
                key={ex.id}
                exercise={ex}
                selected={ex.id === selectedId}
                onClick={() => {
                  setSelectedId(ex.id);
                  // En une colonne, choisir un exercice *est* la navigation
                  // vers sa fiche ; au-delà de 768 px l'état ne peint rien.
                  onCompactPaneChange("detail");
                }}
              />
            ))}
          </div>
          <div className="px-3 py-2 shrink-0" style={{ borderTop: "1px solid var(--color-glass-separator)" }}>
            <p className="text-[11px] text-text-muted">{filtered.length} exercice{filtered.length > 1 ? "s" : ""}</p>
          </div>
        </div>

        {/* Right panel, detail */}
        <div className={`${compactPane === "list" ? "hidden md:flex" : "flex"} flex-1 flex-col min-w-0`}>
          {/* La liste ayant quitté l'écran, rien d'autre ne dit comment y
              revenir : ce retour est la seule porte, et il double la touche
              Échap pour qui a un clavier. */}
          <button type="button" onClick={() => onCompactPaneChange("list")}
            className="md:hidden flex items-center gap-1.5 px-4 py-3 shrink-0 text-[13px] text-text-secondary hover:text-text-primary transition-colors duration-150 cursor-pointer"
            style={{ borderBottom: "1px solid var(--color-glass-separator)" }}>
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7.5 2.5l-3.5 3.5 3.5 3.5" />
            </svg>
            Tous les exercices
          </button>

          {selected ? (
            <ExerciseDetailPanel
              exercise={selected}
              loading={starting}
              onStart={async () => {
                setStarting(true);
                try {
                  const detail = await getExercise(selected.id);
                  onStartExercise(detail);
                } catch (err) {
                  console.error("Failed to load exercise:", err);
                } finally {
                  setStarting(false);
                }
              }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-text-muted">
              <p className="text-[14px]">Sélectionne un exercice</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal wrapper ───

interface PracticeModalProps {
  open: boolean;
  onClose: () => void;
  onStartExercise: (exercise: ExerciseDetail) => void;
}

const TITLE_ICON = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-accent-bright)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2L1.5 5.5 8 9l6.5-3.5z" /><path d="M3 7.5v4l5 3 5-3v-4" />
  </svg>
);

export function PracticeModal({ open, onClose, onStartExercise }: PracticeModalProps) {
  const [compactPane, setCompactPane] = useState<CompactPane>("list");
  const isCompact = useBreakpoint() === "compact";

  /**
   * Le volet vit ici et non dans le contenu, parce qu'il décide aussi de ce que
   * fait Échap.
   *
   * Chaque ouverture repart de la liste : rouvrir directement sur une fiche
   * afficherait un exercice que personne n'a redemandé. Ajusté pendant le
   * rendu plutôt que dans un effet — React relance le rendu avant de peindre,
   * donc l'ancien volet n'atteint jamais l'écran.
   */
  const [prevOpen, setPrevOpen] = useState(open);

  if (prevOpen !== open) {
    setPrevOpen(open);
    setCompactPane("list");
  }

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      /* Échap défait le dernier pas, pas toute la modale : sur une fiche en une
         colonne, ce pas est l'ouverture du détail. La croix et le fond ferment
         toujours. Le battement d'une frame de `useBreakpoint` est sans effet
         ici, la valeur n'étant lue qu'au moment d'une frappe. */
      onEscape={
        isCompact && compactPane === "detail"
          ? () => setCompactPane("list")
          : onClose
      }
      title="Apprendre"
      titleIcon={TITLE_ICON}
      titleExtra={<span className="text-[12px] text-text-muted ml-1">Exercices techniques</span>}
      size="xl"
      maxHeight="min(85vh, 780px)"
    >
      <PracticeContent
        onStartExercise={(exercise) => { onStartExercise(exercise); onClose(); }}
        compactPane={compactPane}
        onCompactPaneChange={setCompactPane}
      />
    </ModalTemplate>
  );
}
